import { Component, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { startWith, map, switchMap } from 'rxjs/operators';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FriendService } from '../search-friend.service';
import { ConnectedPeopleInterface } from '../user-account';
import { DataSharingService } from '../shared/data-sharing.service';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { ClanService } from '../clan.service';
import { SubClan, subClans, PermissionsModel } from '../models/clan-interfaces';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-people-permissions',
  standalone: true,
  imports: [
    FormsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    AsyncPipe,
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatIconModule,
    MatCheckboxModule,
    MatSnackBarModule, // Add this for Snackbar support
    TranslateModule,
  ],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
  template: `<form class="search-form">
      <input
        type="text"
        [placeholder]="'people-permissions.SEARCH_PLACEHOLDER' | translate"
        [formControl]="findPeopleControl"
        [matAutocomplete]="auto"
        class="search-input"
        (optionSelected)="onSelectPerson($event)"
      />
      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="onSelectPerson($event)"
      >
        <mat-option
          *ngFor="let person of filteredPeople | async"
          [value]="person.name"
        >
          {{ person.name }}
        </mat-option>
      </mat-autocomplete>
    </form>

    <div class="radio-group">
      <label>
        <input
          type="radio"
          name="status"
          [value]="2"
          (change)="onStatusChange(2)"
          checked
        />
        {{ 'people-permissions.SHOW_FRIENDS' | translate }}
      </label>
      <label>
        <input
          type="radio"
          name="status"
          [value]="3"
          (change)="onStatusChange(3)"
        />
        {{ 'people-permissions.SHOW_FOLLOWERS' | translate }}
      </label>
    </div>

    <table
      mat-table
      [dataSource]="dataSource_"
      multiTemplateDataRows
      class="mat-elevation-z8"
    >
      <ng-container matColumnDef="family_table_name">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'people-permissions.FAMILY_TABLE_NAME' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.family_table_name }}
        </td>
      </ng-container>
      <ng-container matColumnDef="owner_name">
        <th mat-header-cell *matHeaderCellDef>{{ 'owner_name' }}</th>
        <td mat-cell *matCellDef="let element">
          {{ 'people-permissions.OWNER_NAME' | translate }}
        </td>
      </ng-container>
      <ng-container matColumnDef="expand">
        <th mat-header-cell *matHeaderCellDef aria-label="row actions">
          &nbsp;
        </th>
        <td mat-cell *matCellDef="let element">
          <button
            mat-icon-button
            aria-label="expand row"
            (click)="
              expandedElement = expandedElement === element ? null : element;
              $event.stopPropagation()
            "
          >
            <mat-icon>{{
              expandedElement === element
                ? 'keyboard_arrow_up'
                : 'keyboard_arrow_down'
            }}</mat-icon>
          </button>
        </td>
      </ng-container>
      <ng-container matColumnDef="expandedDetail">
        <td
          mat-cell
          *matCellDef="let element"
          [attr.colspan]="columnsToDisplayWithExpand.length"
        >
          <div
            class="example-element-detail"
            [@detailExpand]="
              element == expandedElement ? 'expanded' : 'collapsed'
            "
          >
            <mat-checkbox
              [(ngModel)]="element.full_access"
              (change)="onCheckboxChange(element, 'full_access', $event)"
            >
              {{ 'people-permissions.FULL_ACCESS' | translate }}
            </mat-checkbox>
            <mat-checkbox
              [(ngModel)]="element.read"
              (change)="onCheckboxChange(element, 'read', $event)"
            >
              {{ 'people-permissions.READ_ACCESS' | translate }}
            </mat-checkbox>
          </div>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="columnsToDisplayWithExpand"></tr>
      <tr
        mat-row
        *matRowDef="let element; columns: columnsToDisplayWithExpand"
        class="example-element-row"
        [class.example-expanded-row]="expandedElement === element"
        (click)="expandedElement = expandedElement === element ? null : element"
      ></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: ['expandedDetail']"
        class="example-detail-row"
      ></tr>
    </table> `,
  styleUrls: ['./people-permissions.component.css'],
  providers: [FriendService, ClanService],
})
export class PeoplePermissionsComponent implements OnInit {
  findPeopleControl = new FormControl(''); // Form control for the input
  connectedPeople: ConnectedPeopleInterface[] = []; // Will hold the connected people data
  filteredPeople!: Observable<ConnectedPeopleInterface[]>; // Observable to hold the filtered results
  selectedStatus = 2; // Default to showing friends
  currentUserId!: string; // Holds the current user ID to exclude them
  selectedSenderId!: string; // Holds the selected person's sender_id
  columnsToDisplay = ['family_table_name', 'owner_name']; // Columns to display
  columnsToDisplayWithExpand = [...this.columnsToDisplay, 'expand'];
  expandedElement: SubClan | null = null; // Track the expanded row
  dataSource_: SubClan[] = subClans; // Data source for the table
  isExpansionDetailRow = (index: number, row: SubClan) =>
    row === this.expandedElement;

  constructor(
    private friendService: FriendService,
    private clanService: ClanService,
    private dataSharingService: DataSharingService, // Inject the DataSharingService for current user info
    private snackBar: MatSnackBar, // Inject MatSnackBar for displaying messages
  ) {}

  ngOnInit() {
    // Fetch the current user ID and then load the friends
    this.dataSharingService.getActiveUserAccount().subscribe((user) => {
      this.currentUserId = user.id; // Get the current user ID
      this.fetchPeople(this.selectedStatus); // Fetch the default status (friends) when the component is initialized
    });
  }

  // Method to fetch people based on selected status
  fetchPeople(status: number) {
    this.filteredPeople = this.friendService.getConnectedPeople(status).pipe(
      switchMap((people) => {
        // Exclude the current user from the list
        this.connectedPeople = people.filter(
          (person) => person.user_id !== this.currentUserId,
        );
        // Listen to value changes and filter connected people based on input
        return this.findPeopleControl.valueChanges.pipe(
          startWith(''), // Start with an empty string
          map((value) => this.filterPeople(value || '')), // Filter people based on input
        );
      }),
    );
  }

  // Triggered when the user changes the radio button selection
  onStatusChange(status: number) {
    this.selectedStatus = status; // Update the selected status
    this.fetchPeople(status); // Fetch people based on the new status
  }

  // Filter method to match the input value with the connected people's names
  private filterPeople(value: string): ConnectedPeopleInterface[] {
    const filterValue = this.normalizeValue(value);
    return this.connectedPeople.filter((person) =>
      this.normalizeValue(person.name).includes(filterValue),
    );
  }

  // Normalize input for case-insensitive and space-insensitive matching
  private normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '');
  }

  // Handler to store the selected person's sender_id
  onSelectPerson(event: any): void {
    console.log('Option selected:', event);
    const selectedPerson = this.connectedPeople.find(
      (person) => person.name === event.option.value,
    );
    if (selectedPerson) {
      this.selectedSenderId = selectedPerson.sender_id; // Store the selected person's sender_id
      this.clanService
        .fetchPermittedClans(this.selectedSenderId)
        .subscribe((clans: SubClan[]) => {
          this.dataSource_ = clans;
        });
      console.log('Selected sender_id:', this.selectedSenderId);
    }
  }

  onCheckboxChange(clan: SubClan, field: keyof SubClan, event: any): void {
    if (!this.selectedSenderId) {
      // Display a message to the user using MatSnackBar
      this.snackBar.open(
        'You should choose a person to give them permission.',
        'Close',
        {
          duration: 3000, // Snackbar will be shown for 3 seconds
          verticalPosition: 'top', // Position the snackbar at the top
        },
      );
      // Revert the checkbox state by setting the value back to its original state
      event.source.checked = !event.checked; // Reverts the checkbox to the previous state
      return; // Exit the function early if selectedSenderId is not set
    }

    if (field === 'full_access' || field === 'read') {
      clan[field] = event.checked as boolean;

      const model: PermissionsModel = {
        id: clan.id, // Using clan.id for permission ID
        owner_id: clan.owner_id, // Assuming clan.owner_id is correctly set
        target_account_id: this.selectedSenderId, // Use the selectedSenderId for target_account_id
        target_Person_id: clan.family_table_id,
        full_access: clan.full_access || false, // Use clan.full_access, default to false if undefined
        read: clan.read || false, // Use clan.read, default to false if undefined
      };

      // Call the method to update the permission
      this.updateClanPermission(model);
    }
  }

  updateClanPermission(permissionData: PermissionsModel) {
    this.clanService.updateClanPermission(permissionData).subscribe(
      (response) => {
        console.log('Permission updated:', response);
      },
      (error) => {
        console.error('Error updating permission:', error);
      },
    );
  }
}
